import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box, Paper, Typography, Button, Table, TableHead, TableRow, TableCell,
  TableBody, TableContainer, Checkbox, Chip, Alert, Dialog, DialogTitle,
  DialogContent, DialogActions, CircularProgress, TextField, Stack,
} from '@mui/material';
import GroupsIcon from '@mui/icons-material/Groups';
import GppMaybeIcon from '@mui/icons-material/GppMaybe';
import { useNavigate } from 'react-router-dom';
import {
  listGDMembers, addGDMembers, removeGDMember, retryMember, getGuardDutyConfig,
  resolveMemberEmails,
} from '../../../services/guarddutyApi';
import { listSecurityHubAccounts } from '../../../services/securityhubApi';
import PortalPageHeader from '../../../components/portal/PortalPageHeader';
import {
  PortalLoading, PortalErrorState, PortalSectionCard,
} from '../../../components/portal/PortalStates';
import OperationProgress from './OperationProgress';
import { POLL_INTERVAL_MS } from './pollInterval';
import { formatStatusLabel } from '../../../utils/statusLabel';
import { motion } from 'motion/react';
import { pageVariants, sectionVariants } from '../../../animations/variants';
import {
  CK, FONT_BODY, sectionCardSx, tableHeadRowSx, headCellSx, rowSx, bodyCellSx,
  statusChipSx, intentFromColor, emptyTextSx, dialogProps, dialogTitleSx,
} from '../portalStyles';

// GuardDuty accent, shared with every other GuardDuty route.
const GD = '#E65100';
const GD_DARK = '#BF360C';

const primaryBtnSx = {
  backgroundColor: GD,
  fontFamily: FONT_BODY,
  fontWeight: 600,
  textTransform: 'none',
  borderRadius: '6px',
  px: 3,
  '&:hover': { backgroundColor: GD_DARK },
};

const textBtnSx = {
  fontFamily: FONT_BODY,
  fontWeight: 600,
  textTransform: 'none',
  borderRadius: '6px',
  color: CK.body,
};

const dangerBtnSx = {
  fontFamily: FONT_BODY,
  fontWeight: 600,
  textTransform: 'none',
  borderRadius: '6px',
  px: 3,
};

// Row actions are pinned to one width so the column has a straight edge whether
// or not a row also offers "Retry".
const rowActionBtnSx = {
  width: 96,
  fontFamily: FONT_BODY,
  fontWeight: 600,
  textTransform: 'none',
  borderRadius: '6px',
};

const enrollmentHeader = (
  <PortalPageHeader
    icon={<GroupsIcon sx={{ fontSize: 24 }} />}
    title="Enrollment"
    subtitle="Manage which member accounts are enrolled in GuardDuty"
    color={GD}
  />
);

// DeploymentField renders one label/value line of the deployment summary.
function DeploymentField({ label, value }) {
  return (
    <Stack
      direction={{ xs: 'column', sm: 'row' }}
      spacing={{ xs: 0.25, sm: 2 }}
      sx={{ py: 0.5, alignItems: { xs: 'flex-start', sm: 'baseline' } }}
    >
      <Typography
        sx={{ fontFamily: FONT_BODY, fontSize: '0.875rem', color: CK.muted, minWidth: { xs: 0, sm: 150 } }}
      >
        {label}
      </Typography>
      <Typography
        sx={{
          fontFamily: FONT_BODY,
          fontSize: '0.875rem',
          fontWeight: 600,
          color: CK.navy,
          wordBreak: 'break-word',
          minWidth: 0,
        }}
      >
        {value}
      </Typography>
    </Stack>
  );
}

const phaseColor = (p) => ({
  enabled: 'success',
  failed: 'error',
  pending: 'default',
  enabling: 'info',
  inviting: 'info',
  accepting: 'info',
  removing: 'warning',
  disabled: 'default',
  // A teardown that failed is a FAILURE, not a neutral resting state. Without
  // this entry the fallback renders it grey, which reads as "removed" — the
  // opposite of the truth, since the account is still in the graph and still
  // carries its grant.
  remove_failed: 'error',
}[p] || 'default');

const activePhases = ['pending', 'enabling', 'inviting', 'accepting', 'removing'];

export default function GuardDutyEnrollmentPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState(null);
  const [members, setMembers] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [confirmRemove, setConfirmRemove] = useState(null);
  const [emailDialog, setEmailDialog] = useState({ open: false, failed: [], manual: {} });
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const pollRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // Config + members determine whether GuardDuty is set up; these must
      // succeed for the page to render the enrollment status.
      const [c, m] = await Promise.all([getGuardDutyConfig(), listGDMembers()]);
      setConfig(c?.data || null);
      setMembers(m?.data || []);
      setError(null);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
    // Candidate-account list is best-effort: never let it blank the page.
    try {
      const a = await listSecurityHubAccounts();
      setAccounts(a?.data?.accounts || a?.data || []);
    } catch (_) {
      setAccounts([]);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  // Start a poll whenever any member enters an active phase.
  // IMPORTANT: do NOT put clearInterval in this effect's cleanup. The interval
  // callback calls setMembers(next) on every tick; that produces a new array
  // reference each time, so [members] changes and React fires the cleanup —
  // killing the interval and restarting its 3-second countdown on every tick.
  // That destroy-and-recreate cycle prevents config.progress from advancing in
  // the rendered bar. Instead the interval stops itself when no active phases
  // remain (inner check below), and a separate unmount effect handles cleanup.
  useEffect(() => {
    const active = members.some((m) => activePhases.includes(m.phase));
    if (active && !pollRef.current) {
      pollRef.current = setInterval(async () => {
        try {
          // Refresh config too so the operation progress bar (config.progress)
          // advances live, not just the per-cell member phases.
          const [m, c] = await Promise.all([listGDMembers(), getGuardDutyConfig()]);
          const next = m?.data || [];
          setMembers(next);
          setConfig(c?.data || null);
          const stillActive = next.some((r) => activePhases.includes(r.phase));
          if (!stillActive && pollRef.current) {
            clearInterval(pollRef.current);
            pollRef.current = null;
          }
        } catch (_) { /* transient */ }
      }, POLL_INTERVAL_MS);
    }
  }, [members]);

  // Stop the interval when the component unmounts.
  useEffect(() => {
    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, []);

  // Loading keeps the header mounted and swaps only the card body, so the page
  // does not jump once the enrollment data lands.
  if (loading) {
    return (
      <motion.div variants={pageVariants} initial="initial" animate="animate">
        <motion.div variants={sectionVariants}>{enrollmentHeader}</motion.div>
        <motion.div variants={sectionVariants}>
          <Paper elevation={0} sx={sectionCardSx}>
            <PortalLoading label="Loading enrollment…" color={GD} />
          </Paper>
        </motion.div>
      </motion.div>
    );
  }
  if (!config || config.status === 'disabled') {
    return (
      <motion.div variants={pageVariants} initial="initial" animate="animate">
        <motion.div variants={sectionVariants}>{enrollmentHeader}</motion.div>
        <motion.div variants={sectionVariants}>
          <Alert
            severity="info"
            sx={{ borderRadius: '8px', fontFamily: FONT_BODY, '& .MuiAlert-message': { fontFamily: FONT_BODY } }}
            action={
              <Button color="inherit" size="small" onClick={() => navigate('/securityhub/guardduty')} sx={{ ...textBtnSx, color: 'inherit' }}>
                Go to setup
              </Button>
            }
          >
            GuardDuty is not set up yet for this customer. Complete the setup wizard first to enroll member accounts.
          </Alert>
        </motion.div>
      </motion.div>
    );
  }

  const worstPhase = (phases) => {
    if (phases.includes('failed')) return 'failed';
    // Ranked below 'failed' deliberately. A row carrying both a failed ENROLMENT
    // and a failed REMOVAL shows 'failed', which is the state that still offers
    // Retry — and the backend's gdRetryRoute decides per row which operation
    // that resumes, so the mixed case cannot re-enrol an account being removed.
    // Without this branch the phase fell through to phases[0] and rendered grey.
    if (phases.includes('remove_failed')) return 'remove_failed';
    if (phases.includes('pending')) return 'pending';
    const active = ['enabling', 'inviting', 'accepting', 'removing'];
    for (const p of active) if (phases.includes(p)) return p;
    if (phases.every((p) => p === 'enabled')) return 'enabled';
    if (phases.every((p) => p === 'disabled')) return 'disabled';
    return phases[0];
  };

  const byAccount = new Map();
  for (const m of members) {
    const prev = byAccount.get(m.accountId) || {
      accountId: m.accountId, phases: [], errors: [],
    };
    prev.phases.push(m.phase);
    if (m.errorMessage) prev.errors.push(`${m.region}: ${m.errorMessage}`);
    byAccount.set(m.accountId, prev);
  }
  // A fully-disabled (previously-removed) member is treated as NOT enrolled: it
  // drops out of the enrolled list and reappears under "Not enrolled" so it can
  // be re-enrolled through the normal Add flow (which resolves its root email).
  const enrolledAccounts = [...byAccount.values()].filter(
    (row) => worstPhase(row.phases) !== 'disabled',
  );
  const enrolledIds = new Set(enrolledAccounts.map((r) => r.accountId));
  const notEnrolled = accounts.filter((a) => !enrolledIds.has(a.accountId));

  const nameById = {};
  accounts.forEach((a) => { if (a.accountId) nameById[a.accountId] = a.name || a.accountName || ''; });
  const acctLabel = (id) => (nameById[id] ? `${nameById[id]} (${id})` : id);

  const submitAdd = async (accountIds, memberEmails) => {
    setBusy(true);
    try {
      await addGDMembers(accountIds, memberEmails);
      setSelected(new Set());
      setEmailDialog({ open: false, failed: [], manual: {} });
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const onAdd = async () => {
    const selectedIds = [...selected];
    setBusy(true);
    try {
      const res = await resolveMemberEmails(selectedIds);
      const failed = (res?.data?.results || []).filter((x) => !x.resolved);
      setBusy(false);
      if (failed.length === 0) {
        await submitAdd(selectedIds, {});
      } else {
        setEmailDialog({ open: true, failed, manual: {} });
      }
    } catch (e) {
      setError(e.message);
      setBusy(false);
    }
  };

  const onConfirmEmails = async () => {
    await submitAdd([...selected], emailDialog.manual);
  };

  const emailDialogComplete = emailDialog.failed.every(
    (f) => (emailDialog.manual[f.accountId] || '').trim() !== '',
  );

  const onRemove = async (accountId) => {
    setBusy(true);
    try {
      await removeGDMember(accountId);
      setConfirmRemove(null);
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const onRetry = async (accountId) => {
    setBusy(true);
    try {
      await retryMember(accountId);
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const linkingRegionsDisplay = (() => {
    try { return JSON.parse(config.linkingRegions || '[]').join(', '); } catch (_) { return ''; }
  })();

  return (
    <motion.div variants={pageVariants} initial="initial" animate="animate">
      <motion.div variants={sectionVariants}>{enrollmentHeader}</motion.div>
      {error && (
        <motion.div variants={sectionVariants}>
          <PortalErrorState error={error} onRetry={() => load()} />
        </motion.div>
      )}

      <motion.div variants={sectionVariants}>
        <PortalSectionCard
          icon={<GppMaybeIcon sx={{ fontSize: 20 }} />}
          title="Deployment"
          subtitle="Where GuardDuty is administered for this organization"
          color={GD}
          sx={{ mb: 3 }}
        >
          <DeploymentField label="Admin account" value={config.adminAccountId} />
          <DeploymentField label="Home region" value={config.homeRegion} />
          <DeploymentField label="Linking regions" value={linkingRegionsDisplay} />
        </PortalSectionCard>
      </motion.div>

      {(config?.status === 'in_progress' || members.some((m) => activePhases.includes(m.phase))) && (
        <motion.div variants={sectionVariants}>
          <OperationProgress percent={config.progress} label={config.progressLabel || 'Working…'} sx={{ mb: 3 }} />
        </motion.div>
      )}

      <motion.div variants={sectionVariants}>
        <PortalSectionCard
          icon={<GroupsIcon sx={{ fontSize: 20 }} />}
          title="Enrolled accounts"
          subtitle={`${enrolledAccounts.length} account${enrolledAccounts.length === 1 ? '' : 's'} protected by GuardDuty`}
          color={GD}
          sx={{ mb: 3 }}
        >
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={tableHeadRowSx}>
                  <TableCell sx={headCellSx}>Account</TableCell>
                  <TableCell sx={headCellSx}>Status</TableCell>
                  <TableCell align="right" sx={headCellSx}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {enrolledAccounts.map((row, index) => {
                  const phase = worstPhase(row.phases);
                  const isAdmin = row.accountId === config.adminAccountId;
                  return (
                    <TableRow key={row.accountId} hover sx={rowSx(index)}>
                      <TableCell sx={bodyCellSx}>
                        {acctLabel(row.accountId)}
                        {isAdmin && <Chip label="admin" size="small" sx={{ ml: 1, ...statusChipSx('neutral') }} />}
                      </TableCell>
                      <TableCell sx={bodyCellSx}>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Chip label={formatStatusLabel(phase)} size="small" sx={statusChipSx(intentFromColor(phaseColor(phase)))} />
                          {activePhases.includes(phase) && <CircularProgress size={14} thickness={5} sx={{ color: GD }} />}
                        </Stack>
                        {row.errors.length > 0 && (
                          <Typography
                            sx={{
                              display: 'block',
                              mt: 0.5,
                              fontFamily: FONT_BODY,
                              fontSize: '0.75rem',
                              color: CK.red,
                              wordBreak: 'break-word',
                            }}
                          >
                            {row.errors.join(' • ')}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="right" sx={bodyCellSx}>
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                          {!isAdmin && phase === 'failed' && (
                            <Button
                              size="small"
                              variant="outlined"
                              color="warning"
                              disabled={busy}
                              onClick={() => onRetry(row.accountId)}
                              sx={rowActionBtnSx}
                            >
                              Retry
                            </Button>
                          )}
                          {!isAdmin && phase !== 'disabled' && phase !== 'removing' && (
                            <Button
                              size="small"
                              variant="outlined"
                              color="error"
                              onClick={() => setConfirmRemove(row.accountId)}
                              sx={rowActionBtnSx}
                            >
                              {/* On a row whose teardown failed this button RESUMES that
                                  teardown rather than starting a new one, so it says so.
                                  "Remove" there would read as "nothing has happened yet". */}
                              {phase === 'remove_failed' ? 'Retry removal' : 'Remove'}
                            </Button>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {enrolledAccounts.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={3} align="center" sx={{ py: 4, borderBottom: 'none' }}>
                      <Typography sx={emptyTextSx}>
                        No accounts enrolled yet — pick accounts below and add them to GuardDuty.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </PortalSectionCard>
      </motion.div>

      <motion.div variants={sectionVariants}>
        <PortalSectionCard
          icon={<GroupsIcon sx={{ fontSize: 20 }} />}
          title="Not enrolled"
          subtitle="Linked accounts that GuardDuty is not watching yet"
          color={GD}
          action={(
            <Button variant="contained" disabled={busy || selected.size === 0} onClick={onAdd} sx={primaryBtnSx}>
              Add {selected.size} to GuardDuty
            </Button>
          )}
        >
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={tableHeadRowSx}>
                  <TableCell padding="checkbox" sx={headCellSx}>
                    <Checkbox
                      sx={{ color: CK.disabled, '&.Mui-checked': { color: GD }, '&.MuiCheckbox-indeterminate': { color: GD } }}
                      indeterminate={notEnrolled.some((a) => selected.has(a.accountId)) && !notEnrolled.every((a) => selected.has(a.accountId))}
                      checked={notEnrolled.length > 0 && notEnrolled.every((a) => selected.has(a.accountId))}
                      disabled={notEnrolled.length === 0}
                      onChange={(e) => setSelected(e.target.checked ? new Set(notEnrolled.map((a) => a.accountId)) : new Set())}
                    />
                  </TableCell>
                  <TableCell sx={headCellSx}>Account</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {notEnrolled.map((a, index) => (
                  <TableRow key={a.accountId} hover sx={rowSx(index)}>
                    <TableCell padding="checkbox" sx={bodyCellSx}>
                      <Checkbox
                        sx={{ color: CK.disabled, '&.Mui-checked': { color: GD } }}
                        checked={selected.has(a.accountId)}
                        onChange={(e) => {
                          const n = new Set(selected);
                          if (e.target.checked) n.add(a.accountId);
                          else n.delete(a.accountId);
                          setSelected(n);
                        }}
                      />
                    </TableCell>
                    <TableCell sx={bodyCellSx}>{a.accountName || a.name || a.accountId} ({a.accountId})</TableCell>
                  </TableRow>
                ))}
                {notEnrolled.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={2} align="center" sx={{ py: 4, borderBottom: 'none' }}>
                      <Typography sx={emptyTextSx}>All linked accounts are already enrolled.</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </PortalSectionCard>
      </motion.div>

      <Dialog open={!!confirmRemove} onClose={() => setConfirmRemove(null)} {...dialogProps}>
        <DialogTitle sx={dialogTitleSx}>Remove account from GuardDuty</DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Typography sx={{ fontFamily: FONT_BODY, fontSize: '0.875rem', color: CK.body, mt: 1, wordBreak: 'break-word' }}>
            Remove <b>{confirmRemove}</b>? GuardDuty in that account will be disabled.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setConfirmRemove(null)} sx={textBtnSx}>Cancel</Button>
          <Button variant="contained" color="error" onClick={() => onRemove(confirmRemove)} disabled={busy} sx={dangerBtnSx}>
            Remove
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={emailDialog.open}
        onClose={() => setEmailDialog({ open: false, failed: [], manual: {} })}
        {...dialogProps}
      >
        <DialogTitle sx={dialogTitleSx}>Enter root emails</DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Typography sx={{ fontFamily: FONT_BODY, fontSize: '0.875rem', color: CK.body, mt: 1, mb: 2 }}>
            We could not auto-fetch the AWS root email for the accounts below.
            Enter each account&apos;s root email to enroll it in GuardDuty.
          </Typography>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {emailDialog.failed.map((f) => (
              <Stack
                key={f.accountId}
                direction={{ xs: 'column', sm: 'row' }}
                spacing={2}
                alignItems={{ xs: 'stretch', sm: 'center' }}
              >
                <Typography
                  sx={{
                    fontFamily: FONT_BODY,
                    fontSize: '0.875rem',
                    fontWeight: 600,
                    color: CK.navy,
                    minWidth: { xs: 0, sm: 220 },
                    wordBreak: 'break-word',
                  }}
                >
                  {acctLabel(f.accountId)}
                </Typography>
                <TextField
                  size="small"
                  required
                  label="Root email"
                  sx={{ flex: 1, minWidth: 0 }}
                  value={emailDialog.manual[f.accountId] || ''}
                  onChange={(e) => setEmailDialog((d) => ({
                    ...d,
                    manual: { ...d.manual, [f.accountId]: e.target.value },
                  }))}
                  helperText={f.reason || 'Auto-fetch failed — enter this account’s AWS root email'}
                  error={!(emailDialog.manual[f.accountId] || '').trim()}
                />
              </Stack>
            ))}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setEmailDialog({ open: false, failed: [], manual: {} })} sx={textBtnSx}>Cancel</Button>
          <Button
            variant="contained"
            onClick={onConfirmEmails}
            disabled={busy || !emailDialogComplete}
            sx={primaryBtnSx}
          >
            Add to GuardDuty
          </Button>
        </DialogActions>
      </Dialog>
    </motion.div>
  );
}
